import { Injectable } from '@angular/core'
import {
  Judge,
  JudgeDetails,
  JudgeProfile,
  PaginatedJudgeSummaries,
  UpdateJudgeProfileRequest,
} from '@ftr/annotations-contracts'
import { AnnotationsApiClientFactory } from '@ftr/api-annotations'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { LoggingService } from '@ftr/ui-observability'
import { AnnotationsService } from '../base/annotations.service'

const MAX_JUDGE_SUMMARIES = 100

@Injectable({
  providedIn: 'root',
})
export class JudgesService extends AnnotationsService {
  constructor(
    apiClientFactory: AnnotationsApiClientFactory,
    regionCache: CourtSystemRegionCache,
    private readonly logger: LoggingService,
  ) {
    super(apiClientFactory, regionCache)
  }

  getJudgeForUser(courtSystemId: Uuid): ApiResult<JudgeProfile | null> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client => client.judges.getJudgeForUser({ query: { courtSystemId } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          this.logger.error(`Failed to get judge for user in court system ${courtSystemId}`)
          return RemoteData.success(null)
        }

        return RemoteData.success<JudgeProfile | null>(result.body)
      },
    })
  }

  getJudgeSummaries({ courtSystemId }: { courtSystemId: Uuid }): ApiResult<PaginatedJudgeSummaries> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client =>
        client.judges.judgeSummaries({
          query: {
            courtSystemId,
            limit: MAX_JUDGE_SUMMARIES,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch judges'))
        }

        return RemoteData.success<PaginatedJudgeSummaries>(result.body)
      },
    })
  }

  getJudgeDetails({ judgeId, courtSystemId }: { judgeId: Uuid; courtSystemId: Uuid }): ApiResult<JudgeDetails> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client => client.judges.judgeDetails({ params: { judgeId } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch judges'))
        }

        return RemoteData.success<JudgeDetails>(result.body)
      },
    })
  }

  updateJudge(judge: UpdateJudgeProfileRequest): ApiResult<Judge> {
    return this.performApiRequest({
      courtSystemId: judge.courtSystemId,
      requestFn: client =>
        client.judges.updateJudge({
          body: judge,
        }),
      responseErrorMapFn: error => {
        if (error.message.includes('JudgeAlreadyExistsForUser')) {
          return RemoteData.failure(error)
        }

        return RemoteData.failure(new Error('Failed to update judge'))
      },
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update judge'))
        }

        return RemoteData.success<Judge>(result.body)
      },
    })
  }
}
